import { fetchAuthWrapper } from '@starjack/api';
import {
    getBackendErrorCode,
    issuedVoucherUrls,
    IssuedVoucherLanguage,
} from '@starjack/entities/issuedVoucher';

const triggerDownload = (filename: string, objectUrl: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', objectUrl);
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

const readErrorCode = async (
    response: Response
): Promise<string | undefined> => {
    try {
        return getBackendErrorCode({ data: await response.json() });
    } catch {
        return undefined;
    }
};

/**
 * Downloads the PDF of an issued voucher through the admin route (the public
 * voucher PDF routes do not serve issued vouchers).
 * Resolves to an error message, or null on success.
 */
export const downloadIssuedVoucherPdf = async (voucher: {
    id: string;
    code: string;
}): Promise<string | null> => {
    try {
        const response = await fetchAuthWrapper(
            issuedVoucherUrls.pdf(voucher.id),
            { headers: { Accept: 'application/pdf' }, method: 'GET' }
        );

        if (!response.ok) {
            const code = await readErrorCode(response);
            return code === 'voucher_not_found'
                ? 'Gutschein nicht gefunden.'
                : 'Fehler beim Download. Bitte später noch einmal versuchen.';
        }

        const objectUrl = URL.createObjectURL(await response.blob());
        triggerDownload(
            `gutschein-${voucher.code || voucher.id}.pdf`,
            objectUrl
        );
        // Revoke asynchronously so the browser has picked up the download.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        return null;
    } catch {
        return 'Fehler beim Download. Bitte später noch einmal versuchen.';
    }
};

export type IssueVoucherPreviewBody = {
    voucherTemplateId: string;
    amount: { amount: number; currency: 'EUR' };
    sender: string;
    receiver: string;
    text: string;
    language: IssuedVoucherLanguage;
};

/**
 * Renders a preview image through the existing public preview endpoint.
 * Resolves to an object URL (caller revokes it) or null on failure.
 */
export const fetchIssueVoucherPreview = async (
    body: IssueVoucherPreviewBody
): Promise<string | null> => {
    try {
        const response = await fetchAuthWrapper(
            issuedVoucherUrls.preview(body.voucherTemplateId),
            {
                method: 'POST',
                headers: { Accept: 'image/jpeg' },
                body: JSON.stringify(body),
            }
        );
        if (!response.ok) return null;
        return URL.createObjectURL(await response.blob());
    } catch {
        return null;
    }
};
